/** Top-down tree with fixed-width boxes: positions come from the tree's shape, never from the DOM. */

export type ReporteRow = {
  employee_id: string
  nombre_completo: string
  cargo: string | null
  area: string | null
  jefe_employee_id: string | null
}

type LayoutNode = {
  employee_id: string
  row: ReporteRow
  depth: number
  x: number
  y: number
}

type LayoutEdge = {
  from: string
  to: string
}

export type TreeLayout = {
  nodes: LayoutNode[]
  edges: LayoutEdge[]
  width: number
  height: number
}

export const BOX_WIDTH = 152
// Room for three single-line rows (name, cargo chip, área); less clips the box.
export const BOX_HEIGHT = 72
const H_GAP = 24
const V_GAP = 48

const collator = new Intl.Collator('es', {sensitivity: 'base'})

/** Never the API's order, which scattered same-cargo siblings; rows without cargo go last so they
 * never split a group. */
function byCargoThenName(a: ReporteRow, b: ReporteRow): number {
  if (a.cargo === null && b.cargo !== null) return 1
  if (a.cargo !== null && b.cargo === null) return -1
  return collator.compare(a.cargo ?? '', b.cargo ?? '') || collator.compare(a.nombre_completo, b.nombre_completo)
}

function byJefeMap(rows: ReporteRow[]): Map<string, ReporteRow[]> {
  const map = new Map<string, ReporteRow[]>()
  for (const row of rows) {
    const key = row.jefe_employee_id ?? '__root__'
    const bucket = map.get(key) ?? []
    bucket.push(row)
    map.set(key, bucket)
  }
  for (const bucket of map.values()) bucket.sort(byCargoThenName)
  return map
}

/** Same root rule as `ReporteView`; the server already breaks cycles in `core/organigrama.py::build_reporte`. */
export function findRoots(rows: ReporteRow[]): ReporteRow[] {
  return rows.filter(
    (row) =>
      row.jefe_employee_id === null ||
      row.jefe_employee_id === row.employee_id ||
      !rows.some((r) => r.employee_id === row.jefe_employee_id)
  )
}

/** The `seen` set stops a jefe chain that loops back on itself. */
export function computeTreeLayout(rows: ReporteRow[]): TreeLayout {
  const byJefe = byJefeMap(rows)
  const roots = findRoots(rows)
  const nodes: LayoutNode[] = []
  const edges: LayoutEdge[] = []

  function subtreeWidth(row: ReporteRow, seen: Set<string>): number {
    if (seen.has(row.employee_id)) return BOX_WIDTH
    const children = (byJefe.get(row.employee_id) ?? []).filter((c) => c.employee_id !== row.employee_id)
    if (children.length === 0) return BOX_WIDTH
    const nextSeen = new Set(seen).add(row.employee_id)
    const childrenWidth = children.reduce((sum, c) => sum + subtreeWidth(c, nextSeen), 0)
    const gaps = (children.length - 1) * H_GAP
    return Math.max(BOX_WIDTH, childrenWidth + gaps)
  }

  function place(row: ReporteRow, depth: number, left: number, seen: Set<string>): number {
    const width = subtreeWidth(row, seen)
    const children = seen.has(row.employee_id)
      ? []
      : (byJefe.get(row.employee_id) ?? []).filter((c) => c.employee_id !== row.employee_id)
    const x = left + width / 2 - BOX_WIDTH / 2
    const y = depth * (BOX_HEIGHT + V_GAP)
    nodes.push({employee_id: row.employee_id, row, depth, x, y})

    if (children.length > 0) {
      const nextSeen = new Set(seen).add(row.employee_id)
      let cursor = left
      for (const child of children) {
        edges.push({from: row.employee_id, to: child.employee_id})
        const childWidth = subtreeWidth(child, nextSeen)
        place(child, depth + 1, cursor, nextSeen)
        cursor += childWidth + H_GAP
      }
    }
    return width
  }

  let cursor = 0
  let maxDepth = 0
  for (const root of roots) {
    const width = subtreeWidth(root, new Set())
    place(root, 0, cursor, new Set())
    cursor += width + H_GAP
  }
  for (const node of nodes) maxDepth = Math.max(maxDepth, node.depth)

  const totalWidth = Math.max(BOX_WIDTH, cursor - H_GAP)
  const totalHeight = (maxDepth + 1) * BOX_HEIGHT + maxDepth * V_GAP

  return {nodes, edges, width: totalWidth, height: totalHeight}
}
